import {
  GridObjectType,
  GridSpaceDataType,
  LineSetType,
  PositionAndDimensionsType,
  PositionType,
  ZoneOutlineData,
  ZoneSpaceShape
} from 'data/types';
import { isPointInPolygon } from 'utils/footprint';
import { buildZoneLabelObstacles, Obstacle } from 'utils/zone-label-obstacles';
import {
  computePerimeter,
  getLabelBounds,
  ZONE_LABEL_HEIGHT_NO_BTUS,
  ZONE_LABEL_HEIGHT_WITH_BTUS,
  ZONE_LABEL_WIDTH
} from 'utils/zone-outline';
import type { LabelPosition } from 'utils/zone-outline';

type Point = { x: number; y: number };

function shapeVertices(shape: ZoneSpaceShape): PositionType[] {
  if (shape.absoluteVertices.length >= 3) return shape.absoluteVertices;
  const { x, y, width, height } = shape.position;
  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height }
  ];
}

export function pointInZoneShapes(
  point: Point,
  shapes: ZoneSpaceShape[]
): boolean {
  return shapes.some((shape) => isPointInPolygon(point, shapeVertices(shape)));
}

export function getEdgeOutwardNormal(
  start: Point,
  end: Point,
  shapes: ZoneSpaceShape[]
): Point | null {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length < 1) return null;

  const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const n1 = { x: -dy / length, y: dx / length };
  const n2 = { x: dy / length, y: -dx / length };
  const probe = 4;
  const outsideFirst = !pointInZoneShapes(
    { x: mid.x + n1.x * probe, y: mid.y + n1.y * probe },
    shapes
  );
  return outsideFirst ? n1 : n2;
}

export function placementFromOutwardNormal(normal: Point): {
  anchor: LabelPosition['anchor'];
  placement: LabelPosition['placement'];
} {
  if (Math.abs(normal.y) >= Math.abs(normal.x)) {
    return {
      anchor: 'middle',
      placement: normal.y < 0 ? 'top' : 'bottom'
    };
  }
  if (normal.x < 0) {
    return { anchor: 'end', placement: 'left' };
  }
  return { anchor: 'start', placement: 'right' };
}

function sideBonus(placement: LabelPosition['placement']): number {
  if (placement === 'top') return 0.4;
  if (placement === 'bottom') return 0.2;
  return 0.3;
}

function isLabelBlocked(
  labelX: number,
  labelY: number,
  labelWidth: number,
  labelHeight: number,
  anchor: LabelPosition['anchor'],
  placement: LabelPosition['placement'],
  obstacles: Obstacle[]
): boolean {
  const bounds = getLabelBounds(
    labelX,
    labelY,
    labelWidth,
    labelHeight,
    anchor,
    placement
  );
  return obstacles.some((obs) => {
    const obsRight = obs.x + obs.width;
    const obsBottom = obs.y + obs.height;
    return (
      bounds.left < obsRight &&
      bounds.right > obs.x &&
      bounds.top < obsBottom &&
      bounds.bottom > obs.y
    );
  });
}

function countLabelOverlaps(
  labelX: number,
  labelY: number,
  labelWidth: number,
  labelHeight: number,
  anchor: LabelPosition['anchor'],
  placement: LabelPosition['placement'],
  obstacles: Obstacle[]
): number {
  const bounds = getLabelBounds(
    labelX,
    labelY,
    labelWidth,
    labelHeight,
    anchor,
    placement
  );
  return obstacles.filter((obs) => {
    const obsRight = obs.x + obs.width;
    const obsBottom = obs.y + obs.height;
    return (
      bounds.left < obsRight &&
      bounds.right > obs.x &&
      bounds.top < obsBottom &&
      bounds.bottom > obs.y
    );
  }).length;
}

function ringsFromShapes(spaceShapes: ZoneSpaceShape[]): Point[][] {
  const perimeter = computePerimeter(spaceShapes);
  if (perimeter.length > 0) return perimeter;
  return spaceShapes.map(shapeVertices).filter((verts) => verts.length >= 3);
}

function aabbFromShapes(spaceShapes: ZoneSpaceShape[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  spaceShapes.forEach((shape) => {
    shapeVertices(shape).forEach((point) => {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    });
  });
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

export function findBestLabelPosition(
  spaceShapes: ZoneSpaceShape[],
  allGridSpaces: GridSpaceDataType[],
  padding: number = 8,
  objects: GridObjectType[] = [],
  lineSets: LineSetType[] = [],
  hasBTUs: boolean = true,
  zoneSpacePositions: PositionAndDimensionsType[] = [],
  additionalObstacles: Obstacle[] = [],
  levelUniqueId?: string
): LabelPosition {
  if (spaceShapes.length === 0) {
    return { x: 0, y: 0, anchor: 'start', placement: 'top' };
  }

  const labelHeight = hasBTUs
    ? ZONE_LABEL_HEIGHT_WITH_BTUS
    : ZONE_LABEL_HEIGHT_NO_BTUS;
  const labelWidth = ZONE_LABEL_WIDTH;
  const obstacles = buildZoneLabelObstacles({
    spaces: allGridSpaces,
    objects,
    lineSets,
    zoneSpacePositions,
    additionalObstacles,
    levelUniqueId
  });

  const candidates: Array<{ pos: LabelPosition; score: number }> = [];
  const tryPosition = (
    x: number,
    y: number,
    anchor: LabelPosition['anchor'],
    placement: LabelPosition['placement'],
    baseScore: number
  ) => {
    const blocked = isLabelBlocked(
      x,
      y,
      labelWidth,
      labelHeight,
      anchor,
      placement,
      obstacles
    );
    candidates.push({
      pos: { x, y, anchor, placement },
      score: blocked ? 0 : baseScore
    });
  };

  const paddingLevels = [padding, padding + 8, padding + 16];
  const rings = ringsFromShapes(spaceShapes);

  rings.forEach((ring) => {
    for (let i = 0; i < ring.length; i += 1) {
      const start = ring[i];
      const end = ring[(i + 1) % ring.length];
      const normal = getEdgeOutwardNormal(start, end, spaceShapes);
      if (normal) {
        const mid = {
          x: (start.x + end.x) / 2,
          y: (start.y + end.y) / 2
        };
        const { anchor, placement } = placementFromOutwardNormal(normal);
        paddingLevels.forEach((distance, index) => {
          tryPosition(
            mid.x + normal.x * distance,
            mid.y + normal.y * distance,
            anchor,
            placement,
            5 - index * 2 + sideBonus(placement)
          );
        });
      }
    }
  });

  const aabb = aabbFromShapes(spaceShapes);
  if (aabb) {
    const { minX, minY, maxX, maxY } = aabb;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    tryPosition(centerX, minY - padding, 'middle', 'top', 2.2);
    tryPosition(centerX, maxY + padding, 'middle', 'bottom', 1.8);
    tryPosition(minX - padding, centerY, 'end', 'left', 1.8);
    tryPosition(maxX + padding, centerY, 'start', 'right', 1.8);
  }

  const unblocked = candidates.filter((candidate) => candidate.score > 0);
  if (unblocked.length > 0) {
    unblocked.sort((a, b) => b.score - a.score);
    return unblocked[0].pos;
  }

  if (candidates.length === 0) {
    return { x: 0, y: 0, anchor: 'start', placement: 'top' };
  }

  let bestCandidate = candidates[0];
  let bestOverlaps = Infinity;
  for (const candidate of candidates) {
    const overlaps = countLabelOverlaps(
      candidate.pos.x,
      candidate.pos.y,
      labelWidth,
      labelHeight,
      candidate.pos.anchor,
      candidate.pos.placement,
      obstacles
    );
    if (overlaps < bestOverlaps) {
      bestOverlaps = overlaps;
      bestCandidate = candidate;
    }
  }

  return bestCandidate.pos;
}

export function placeZoneOutlineLabels(
  outlines: ZoneOutlineData[],
  gridSpaces: GridSpaceDataType[],
  objects: GridObjectType[],
  lineSets: LineSetType[],
  levelUniqueId?: string
): ZoneOutlineData[] {
  const placedLabelObstacles: Obstacle[] = [];

  return outlines.map((outline) => {
    const hasBTUs = outline.BTUs.heating > 0 || outline.BTUs.cooling > 0;
    const labelPosition = findBestLabelPosition(
      outline.spaceShapes,
      gridSpaces,
      8,
      objects,
      lineSets,
      hasBTUs,
      outline.spacePositions,
      placedLabelObstacles,
      levelUniqueId
    );

    const labelHeight = hasBTUs
      ? ZONE_LABEL_HEIGHT_WITH_BTUS
      : ZONE_LABEL_HEIGHT_NO_BTUS;
    const bounds = getLabelBounds(
      labelPosition.x,
      labelPosition.y,
      ZONE_LABEL_WIDTH,
      labelHeight,
      labelPosition.anchor,
      labelPosition.placement
    );
    placedLabelObstacles.push({
      x: bounds.left,
      y: bounds.top,
      width: bounds.right - bounds.left,
      height: bounds.bottom - bounds.top
    });

    return { ...outline, labelPosition };
  });
}
